import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import {
    IllustrationBalancing,
    IllustrationBulb,
    Module3S1Exercise4Illustration3,
    Module3S1Exercise4Illustration4,
    ThoughtsBalancing,
} from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page3TextInputKey = 'exercise314Page3TextInputKey';
const page4TextInputKey = 'exercise314Page4TextInputKey';
const page5TextInputKey = 'exercise314Page5TextInputKey';

export const exerciseData314: ExerciseData = {
    ExerciseView: Module3S1Exercise4,
    dataKeys: [page3TextInputKey, page4TextInputKey, page5TextInputKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Det der dræner dig',
            description:
                'Overvej og notér hvad du har i din hverdag, der bruger energi. F.eks. skole, forældres skilsmisse, en sygdom, en bestemt situation på sociale medier.',
            dataKeys: [page3TextInputKey],
        }),
        createQuestionFromConfiguration({
            title: 'Det der lader dig op',
            description:
                'Overvej nu, hvad du har i din hverdag, der giver dig energi. Eller overvej, hvad der kunne blive en del af det. Du kan f.eks. tænke på nogle af de ting, du har noteret i modul 2, omkring selvomsorg. Typen af aktivitet er ikke så vigtig. Det handler om, hvad der for dig bare føles rart, og hvor du ikke skal præstere.',
            dataKeys: [page4TextInputKey],
        }),
        createQuestionFromConfiguration({
            title: 'Noter din daglige ændring',
            description:
                'Det kan være noget, du gerne vil begynde på, stoppe med eller gøre mere af.',
            dataKeys: [page5TextInputKey],
        }),
    ],
};

export function Module3S1Exercise4({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Balance">
            <ProgressBar current={exercise.CurrentPage} total={6} />
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            case 1:
                return (
                    <Page1
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <Page3
                        textInput={exercise.Data[page3TextInputKey] ?? ''}
                        onContinue={(text) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 4,
                                data: {
                                    [page3TextInputKey]: normalizeInput(text),
                                },
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <Page4
                        textInput={exercise.Data[page4TextInputKey] ?? ''}
                        onContinue={(text) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 5,
                                data: {
                                    [page4TextInputKey]: normalizeInput(text),
                                },
                            });
                        }}
                    />
                );
            case 5:
                return (
                    <Page5
                        textInput={exercise.Data[page5TextInputKey] ?? ''}
                        whatDrainsYou={exercise.Data[page3TextInputKey] ?? ''}
                        whatChargesYou={exercise.Data[page4TextInputKey] ?? ''}
                        onContinue={(text) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 6,
                                data: {
                                    [page5TextInputKey]: normalizeInput(text),
                                },
                            });
                        }}
                    />
                );
            case 6:
                return (
                    <Page6
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
        }
    }
}

type PageProps = {
    onContinue: () => void;
};

function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Find balancen</LabelDisplay2>
            <LabelBody>
                Vi har hver især kun en bestemt mængde energi, der kan variere
                over tid. Forskellige aktiviteter koster forskellige mængder af
                energi, og det kan være en god idé, at tjekke ind med de valg
                man tager i hverdagen. Når vi har for meget og er for
                overvældede, kan det nemlig intensivere en tendens til den
                negative spiral af overtænkning.
            </LabelBody>
            <IllustrationBalancing className="mx-auto my-30" />
            <LabelBody class="mb-30">
                Derfor har vi hver især brug for at tjekke ind med, hvor vi kan
                frigive noget energi, så vi kan stå imod de ting, vi møder på
                vores vej. Det betyder ikke, at vi ikke skal blive påvirket, af
                det vi oplever, men nærmere at vi kan hjælpe os selv med at
                vurdere, om vi har brug for at prioritere mere tid til det, der
                lader os op.
            </LabelBody>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Hvordan fordeler du din energi?
            </LabelDisplay2>
            <LabelBody>
                Hvis du kun prioriterer plads til det, der tager energi fra dig,
                kan du miste balancen. I denne øvelse skal du overveje, hvad du
                balancerer med: Kig på det der dræner, og det der lader dig op.
            </LabelBody>
            <ThoughtsBalancing className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Lad mig komme i gang
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    textInput: string;
    onContinue: (text: string) => void;
};

function Page3({ textInput, onContinue }: Page3Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <Card class="mb-30">
                <Module3S1Exercise4Illustration3 className="mb-20" />
                <LabelDisplay2 class="mb-10">Det der dræner dig</LabelDisplay2>
                <LabelBody>
                    Overvej og notér hvad du har i din hverdag, der bruger
                    energi. F.eks. skole, forældres skilsmisse, en sygdom, en
                    bestemt situation på sociale medier.
                </LabelBody>
                <Textarea text={text} onChangeText={setText} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={() => onContinue(text)}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page4Props = {
    textInput: string;
    onContinue: (text: string) => void;
};

function Page4({ textInput, onContinue }: Page4Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <Card class="mb-30">
                <Module3S1Exercise4Illustration4 />
                <LabelDisplay2 class="mb-10">
                    Det der lader dig op
                </LabelDisplay2>
                <LabelBody>
                    Overvej nu, hvad du har i din hverdag, der giver dig energi.
                    Eller overvej, hvad der kunne blive en del af det. Du kan
                    f.eks. tænke på nogle af de ting, du har noteret i modul 2,
                    omkring selvomsorg. Typen af aktivitet er ikke så vigtig.
                    Det handler om, hvad der for <b>dig</b> bare føles rart, og
                    hvor du ikke skal præstere.
                </LabelBody>
                <Textarea text={text} onChangeText={setText} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={() => onContinue(text)}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page5Props = {
    textInput: string;
    whatDrainsYou: string;
    whatChargesYou: string;
    onContinue: (text: string) => void;
};

function Page5({
    textInput,
    whatDrainsYou,
    whatChargesYou,
    onContinue,
}: Page5Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <LabelDisplay1 class="mb-10">Super!</LabelDisplay1>
            <LabelBody class="mb-30">
                Som afrunding på øvelsen anbefaler vi, at du vælger én lille
                ting, du gerne vil ændre i din daglige rutine for at fordele din
                energi bedst.
            </LabelBody>
            {whatDrainsYou && (
                <>
                    <LabelBodyHeadline color="text-dark-red">
                        Det der dræner dig
                    </LabelBodyHeadline>
                    <LabelBody class="mb-30">{whatDrainsYou}</LabelBody>
                </>
            )}
            {whatChargesYou && (
                <>
                    <LabelBodyHeadline color="text-dark-red">
                        Det der lader dig op
                    </LabelBodyHeadline>
                    <LabelBody class="mb-30">{whatChargesYou}</LabelBody>
                </>
            )}
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Noter din daglige ændring
                </LabelDisplay1>
                <LabelBody>
                    Det kan være noget, du gerne vil begynde på, stoppe med
                    eller gøre mere af.
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue(text);
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page6({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Hvis vi ikke prioriterer tid til det, der giver os energi, så
                bruger vi mere energi, end vi har. Balanceøvelsen er et redskab,
                du kan tage frem flere gange i løbet af dit liv, som hjælp til
                at tjekke ind med, om der er noget, du har brug for mere eller
                mindre af.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
